import { ARGUS } from "./argus";
import { CERBERUS } from "./cerberus";
import { DIRECTORY_PAGES } from "./directories";
import { DOCS_PAGE } from "./docs";
import { INDUSTRY_PAGES } from "./industries";
import { MIMIR } from "./mimir";
import { PILLAR_PAGES } from "./pillars";
import { ALL_PUBLICATIONS, RESEARCH_PAGE } from "./research";
import { GLOSSARY_PAGE, LIBRARY_PAGE } from "./resources";
import { ADVISORY_SERVICES } from "./services-advisory";
import { ASSURANCE_SERVICES } from "./services-assurance";
import { COMPLIANCE_SERVICES } from "./services-compliance";
import { DEPLOYMENT_SERVICES } from "./services-deployment";
import { GOVERNANCE_SERVICES } from "./services-governance";
import { SOLUTION_PAGES } from "./solutions";
import { TRACELOCK } from "./tracelock";
import { WARDEN_HUB, WARDEN_PRACTICES } from "./warden";
import type {
  DirectoryPageContent,
  GlossaryPageContent,
  LibraryPageContent,
  OutcomePageContent,
  PillarPageContent,
  ProductPageContent,
  Publication,
  ResearchPageContent,
  ServicePageContent,
  WardenHubContent,
} from "./types";

/**
 * Product pages that have real content. Anything absent here still routes —
 * it renders the labeled placeholder — so content can land product by product
 * without the navigation waiting on it.
 */
export const PRODUCT_PAGES: ProductPageContent[] = [
  CERBERUS,
  ARGUS,
  MIMIR,
  TRACELOCK,
];

export function productPage(slug: string): ProductPageContent | undefined {
  return PRODUCT_PAGES.find((p) => p.slug === slug);
}

export function pillarPage(path: string): PillarPageContent | undefined {
  return PILLAR_PAGES.find((p) => p.path === path);
}

/** Solutions and industries share a template, so they share a lookup. */
export const OUTCOME_PAGES: OutcomePageContent[] = [
  ...SOLUTION_PAGES,
  ...INDUSTRY_PAGES,
];

export function outcomePage(path: string): OutcomePageContent | undefined {
  return OUTCOME_PAGES.find((p) => p.path === path);
}

export function directoryPage(path: string): DirectoryPageContent | undefined {
  return DIRECTORY_PAGES.find((p) => p.path === path);
}

/**
 * Warden service pages, ordered by practice so the hub and the menu read the
 * same way. `WARDEN_SERVICES` in navigation.ts stays the source of truth for
 * which services exist; a test asserts every one of them has content here.
 */
export const SERVICE_PAGES: ServicePageContent[] = [
  ...ASSURANCE_SERVICES,
  ...GOVERNANCE_SERVICES,
  ...COMPLIANCE_SERVICES,
  ...DEPLOYMENT_SERVICES,
  ...ADVISORY_SERVICES,
];

export function servicePage(path: string): ServicePageContent | undefined {
  return SERVICE_PAGES.find((p) => p.path === path);
}

export function wardenHub(path: string): WardenHubContent | undefined {
  return path === WARDEN_HUB.path ? WARDEN_HUB : undefined;
}

/**
 * The resource library and the documentation signpost share a template, so a
 * single lookup covers both. Adding a third index page is a content change
 * rather than a routing one.
 */
export const LIBRARY_PAGES: LibraryPageContent[] = [LIBRARY_PAGE, DOCS_PAGE];

export function libraryPage(path: string): LibraryPageContent | undefined {
  return LIBRARY_PAGES.find((p) => p.path === path);
}

export function researchPage(path: string): ResearchPageContent | undefined {
  return path === RESEARCH_PAGE.path ? RESEARCH_PAGE : undefined;
}

export function glossaryPage(path: string): GlossaryPageContent | undefined {
  return path === GLOSSARY_PAGE.path ? GLOSSARY_PAGE : undefined;
}

export {
  ALL_PUBLICATIONS,
  DIRECTORY_PAGES,
  DOCS_PAGE,
  GLOSSARY_PAGE,
  INDUSTRY_PAGES,
  LIBRARY_PAGE,
  PILLAR_PAGES,
  RESEARCH_PAGE,
  SOLUTION_PAGES,
  WARDEN_HUB,
  WARDEN_PRACTICES,
};
export type {
  DirectoryPageContent,
  GlossaryPageContent,
  LibraryPageContent,
  OutcomePageContent,
  PillarPageContent,
  ProductPageContent,
  Publication,
  ResearchPageContent,
  ServicePageContent,
  WardenHubContent,
};
